/**
 * View Management Service
 * Handles MS Project view detection and custom content rendering
 */

/* global console, document, Office, window, setTimeout */

import { msProjectService } from "./msProjectService.js";

class ViewManagementService {
  constructor() {
    this.currentView = null;
    this.viewHistory = [];
  }

  extractFieldValue(fieldValue) {
    if (!fieldValue) return null;

    if (typeof fieldValue === "object" && fieldValue.fieldValue !== undefined) {
      return fieldValue.fieldValue;
    }

    return fieldValue;
  }

  /**
   * Remove curly braces from GUID string
   * Converts "{8C7D47B8-B684-F011-9F5C-58CE2A1B19AC}" to "8C7D47B8-B684-F011-9F5C-58CE2A1B19AC"
   */
  cleanGuid(guid) {
    if (!guid || typeof guid !== 'string') return guid;
    return guid.replace(/[{}]/g, '');
  }

  processTaskData(taskData) {
    const processedTask = {};

    for (const [key, value] of Object.entries(taskData)) {
      if (typeof value === "object" && value !== null && value.fieldValue !== undefined) {
        processedTask[key] = value.fieldValue;
      } else {
        processedTask[key] = value;
      }
    }

    return processedTask;
  }

  /**
   * Get active view from MS Project
   * @returns {Promise<Object>} Active view information
   */
  async getActiveView() {
    return new Promise((resolve, reject) => {
      if (!Office || !Office.context || !Office.context.document) {
        reject(new Error("Office context not available"));
        return;
      }

      Office.context.document.getSelectedViewAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const viewType = result.value.viewType;
          const viewName = result.value.viewName;

          this.currentView = {
            viewType,
            viewName,
            timestamp: new Date().toISOString(),
          };

          resolve(this.currentView);
        } else {
          reject(new Error(result.error.message));
        }
      });
    });
  }

  /**
   * Test function to get selected view information
   * @returns {Promise<void>}
   */
  async testGetSelectedView() {
    try {
      console.log("Testing Office.context.document.getSelectedViewAsync...");

      if (!Office || !Office.context || !Office.context.document) {
        console.log("Office context not available");
        return;
      }

      Office.context.document.getSelectedViewAsync((result) => {
        console.log("getSelectedViewAsync result:", result);

        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const viewType = result.value.viewType;
          const viewName = result.value.viewName;

          console.log(`Active view: ${viewName} (${viewType})`);

          switch (viewType) {
            case 1:
              this.handleGanttView();
              break;
            case 3:
              this.handleResourceSheet();
              break;
            case 4:
              this.handleResourceUsage();
              break;
            case 0:
              this.handleTaskSheet();
              break;
            default:
              console.log("Unsupported or unadapted view.");
              break;
          }
        } else {
          console.error("Error identifying view:", result.error.message);
        }
      });
    } catch (error) {
      console.error("Error in testGetSelectedView:", error);
    }
  }

  /**
   * Handle Gantt Chart view
   */
  handleGanttView() {
    console.log("Gantt view detected - task loading deferred to first message");
    // OPTIMIZATION: Task loading is now deferred to first message
    // via chatService.checkAndUpdateProjectContext()
  }

  /**
   * Handle Resource Sheet view
   */
  handleResourceSheet() {
    console.log("Performing resource sheet specific operations...");
  }

  /**
   * Handle Resource Usage view
   */
  handleResourceUsage() {
    console.log("Performing resource usage specific operations...");
  }

  /**
   * Handle Task Sheet view
   */
  handleTaskSheet() {
    console.log("Performing task sheet specific operations...");
  }

  /**
   * Get all tasks with throttling for large projects
   */
  async getAllTasksWithThrottling() {
    console.log("Starting to fetch all tasks (with Throttling)...");

    try {
      const maxIndex = await msProjectService.getMaxTaskIndex();
      const tasks = [];
      let completedTasks = 0;
      let currentIndex = 0;
      const batchSize = 3;
      const delayBetweenBatches = 500;

      console.log(`Found ${maxIndex + 1} tasks in project`);

      if (maxIndex < 0) {
        console.log("No tasks in project");
        window.allProjectTasks = [];
        return;
      }

      const processBatch = () => {
        const endIndex = Math.min(currentIndex + batchSize, maxIndex + 1);
        console.log(`Processing tasks ${currentIndex} to ${endIndex - 1}...`);

        for (let i = currentIndex; i < endIndex; i++) {
          setTimeout(
            () => {
              Office.context.document.getTaskByIndexAsync(i, (taskIdResult) => {
                if (taskIdResult.status === Office.AsyncResultStatus.Succeeded) {
                  const taskId = taskIdResult.value;

                  Office.context.document.getTaskAsync(taskId, (taskResult) => {
                    if (taskResult.status === Office.AsyncResultStatus.Succeeded) {
                      const task = taskResult.value;

                      Office.context.document.getTaskFieldAsync(
                        taskId,
                        Office.ProjectTaskFields.Name,
                        (nameResult) => {
                          Office.context.document.getTaskFieldAsync(
                            taskId,
                            Office.ProjectTaskFields.Duration,
                            (durationResult) => {
                              Office.context.document.getTaskFieldAsync(
                                taskId,
                                Office.ProjectTaskFields.Predecessors,
                                (predResult) => {
                                  Office.context.document.getTaskFieldAsync(
                                    taskId,
                                    Office.ProjectTaskFields.Successors,
                                    () => {
                                      const taskName =
                                        nameResult.status === Office.AsyncResultStatus.Succeeded
                                          ? this.extractFieldValue(nameResult.value)
                                          : task.name || `Task ${i}`;

                                      const rawTaskData = {
                                        index: i,
                                        id: this.cleanGuid(taskId),
                                        name: taskName,
                                        start: null,
                                        finish: null,
                                        percentComplete: task.percentComplete,
                                        isCritical: task.isCritical,
                                        duration:
                                          durationResult.status ===
                                            Office.AsyncResultStatus.Succeeded
                                            ? durationResult.value
                                            : null,
                                        predecessors:
                                          predResult.status === Office.AsyncResultStatus.Succeeded
                                            ? predResult.value
                                            : null,
                                        successors: null,
                                        cost: task.cost,
                                        work: task.work,
                                        outlineLevel: null,
                                        summary: null,
                                        isMilestone: task.isMilestone,
                                        isActive: task.isActive,
                                        isHidden: task.isHidden,
                                        retrieved_at: new Date().toISOString(),
                                      };

                                      const taskData = this.processTaskData(rawTaskData);

                                      tasks.push(taskData);
                                      completedTasks++;

                                      console.log(
                                        `Task ${i} loaded: ${taskData.name || `Task ${i}`}`
                                      );

                                      Office.context.document.getTaskFieldAsync(
                                        taskId,
                                        Office.ProjectTaskFields.Start,
                                        (startResult) => {
                                          const startDate =
                                            startResult.status ===
                                              Office.AsyncResultStatus.Succeeded
                                              ? startResult.value.fieldValue
                                              : null;

                                          Office.context.document.getTaskFieldAsync(
                                            taskId,
                                            Office.ProjectTaskFields.Finish,
                                            (finishResult) => {
                                              const finishDate =
                                                finishResult.status ===
                                                  Office.AsyncResultStatus.Succeeded
                                                  ? finishResult.value.fieldValue
                                                  : null;

                                              console.log(
                                                `Task ${taskId}: Start = ${startDate}, Finish = ${finishDate}`
                                              );

                                              taskData.start = startDate;
                                              taskData.finish = finishDate;
                                            }
                                          );
                                        }
                                      );

                                      Office.context.document.getTaskFieldAsync(
                                        taskId,
                                        Office.ProjectTaskFields.Successors,
                                        (succResult) => {
                                          const successors =
                                            succResult.status === Office.AsyncResultStatus.Succeeded
                                              ? succResult.value.fieldValue
                                              : "";
                                          console.log(`Task ${i} Successors: ${successors}`);

                                          taskData.successors = successors;
                                        }
                                      );

                                      Office.context.document.getTaskFieldAsync(
                                        taskId,
                                        Office.ProjectTaskFields.OutlineLevel,
                                        (outlineResult) => {
                                          const outlineLevel =
                                            outlineResult.status ===
                                              Office.AsyncResultStatus.Succeeded
                                              ? outlineResult.value.fieldValue
                                              : 1;
                                          console.log(
                                            `Task ${i} Outline Level: ${outlineLevel}`
                                          );

                                          taskData.outlineLevel = outlineLevel;
                                        }
                                      );

                                      Office.context.document.getTaskFieldAsync(
                                        taskId,
                                        Office.ProjectTaskFields.Summary,
                                        (summaryResult) => {
                                          const isSummary =
                                            summaryResult.status ===
                                              Office.AsyncResultStatus.Succeeded
                                              ? summaryResult.value.fieldValue
                                              : false;
                                          console.log(`Task ${i} is summary: ${isSummary}`);

                                          taskData.summary = isSummary;
                                        }
                                      );

                                      if (completedTasks === maxIndex + 1) {
                                        window.allProjectTasks = tasks;
                                        console.log(`Finished! Loaded ${tasks.length} tasks`);

                                        this.enrichTasksWithBasicDetails(tasks);
                                      }
                                    }
                                  );
                                }
                              );
                            }
                          );
                        }
                      );
                    } else {
                      console.error(`Error fetching task ${i}:`, taskResult.error);
                      completedTasks++;

                      if (completedTasks === maxIndex + 1) {
                        window.allProjectTasks = tasks;
                        console.log(`Finished! Loaded ${tasks.length} tasks (with errors)`);
                      }
                    }
                  });
                } else {
                  console.error(`Error fetching task ID ${i}:`, taskIdResult.error);
                  completedTasks++;

                  if (completedTasks === maxIndex + 1) {
                    console.log(`Finished! Loaded ${tasks.length} tasks (with errors)`);
                    window.allProjectTasks = tasks;
                  }
                }
              });
            },
            (i - currentIndex) * 200
          );
        }

        currentIndex = endIndex;

        if (currentIndex <= maxIndex) {
          setTimeout(processBatch, delayBetweenBatches);
        }
      };

      processBatch();
    } catch (error) {
      console.error("Error fetching tasks:", error);
      window.allProjectTasks = [];
    }
  }

  /**
   * Enrich tasks with basic details
   * @param {Array} tasks - Array of tasks
   * @returns {Promise<Array>} Enriched tasks
   */
  async enrichTasksWithBasicDetails(tasks) {
    try {
      console.log("Enriching tasks with basic details...");

      const enrichedTasks = tasks.map((task) => {
        return {
          ...task,
        };
      });

      window.allProjectTasks = enrichedTasks;

      console.log("Tasks enriched with basic details:");
      console.log(JSON.stringify(enrichedTasks, null, 2));

      if (window.onProjectDataLoaded) {
        window.onProjectDataLoaded(enrichedTasks);
      }

      return enrichedTasks;
    } catch (error) {
      console.error("Error enriching tasks with basic details:", error);
      return tasks;
    }
  }
}

export const viewManagementService = new ViewManagementService();
export default viewManagementService;
